import React from 'react';
import { ChevronDown } from 'lucide-react';

// Variant option labels store USD prices inline, e.g. "(+$9.99)" or "($74.99)".
// When the shopper has selected a non-USD currency we must convert those
// embedded amounts into their chosen currency so they aren't confused into
// thinking "+$9.99" means "+9.99 CHF". `formatPrice` always converts USD →
// shopper-currency, so we extract every $X.XX token and re-render it.
function reformatPricesInLabel(label, formatPrice) {
  if (!label || typeof label !== 'string' || typeof formatPrice !== 'function') return label;
  // Matches "$9.99", "+$9.99", "$1,299.00" — captures sign+amount.
  return label.replace(/\+?\$\s?([0-9]+(?:,[0-9]{3})*(?:\.[0-9]+)?)/g, function (match, num) {
    var hadPlus = match.charAt(0) === '+';
    var amount = parseFloat(num.replace(/,/g, ''));
    if (!isFinite(amount)) return match;
    try {
      var formatted = formatPrice(amount);
      return hadPlus ? '+' + formatted : formatted;
    } catch (_e) {
      return match;
    }
  });
}

function VariantGroup(props) {
  var group = props.group;
  var index = props.index;
  var onChange = props.onChange;
  var formatPrice = props.formatPrice;
  var optionEls = [React.createElement('option', { key: '__empty', value: '' }, 'Select ' + group.name)];
  for (var i = 0; i < group.options.length; i++) {
    var raw = group.options[i];
    var display = reformatPricesInLabel(raw, formatPrice);
    // The `value` MUST stay as the original USD-priced string so calcTotal's
    // regex match (which looks for the literal "+$X.XX") still works and the
    // cart receives a stable variant identifier regardless of currency.
    optionEls.push(React.createElement('option', { key: raw, value: raw }, display));
  }

  var customNote = null;
  if (group.name === 'CUSTOM COLOR' || group.name === 'CUSTOM COLORS' || group.name === 'COLOR PERSONALIZADO' || group.name === 'カスタムカラー' || group.name === 'INDIVIDUELLE FARBE' || group.name === 'COULEUR PERSONNALISEE' || group.name === 'COLORE PERSONALIZZATO') {
    customNote = React.createElement('p', { className: 'mt-2 text-neutral-500 text-sm italic' },
      'With custom color, each mute features a one-of-a-kind marble pattern \u2014 rich swirls of color that flow organically through the material, making every piece a unique work of art. No two are ever alike.'
    );
  }

  return React.createElement('div', null,
    React.createElement('label', { className: 'text-xs uppercase tracking-widest text-neutral-600 mb-3 font-semibold block' },
      group.name, ' ', React.createElement('span', { className: 'text-red-400' }, '*')
    ),
    React.createElement('div', { className: 'relative' },
      React.createElement('select', {
        'data-testid': 'variant-select-' + index,
        onChange: function(e) { if (onChange) onChange(index, e.target.value); },
        className: 'w-full appearance-none bg-neutral-900 border border-white/10 rounded-xl px-5 py-4 text-white text-lg focus:border-[#d4af37] focus:outline-none cursor-pointer'
      }, optionEls),
      React.createElement(ChevronDown, { className: 'absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400 pointer-events-none' })
    ),
    customNote
  );
}

function calcTotal(selections, basePrice) {
  var total = basePrice;
  var addons = 0;
  var hasAnySelection = false;
  var overridePrice = null;

  // Multi-format uplift parser — the same variant is rendered differently
  // per language when it goes through translation:
  //   en/es/ja: (+$9.99)   ← "$" prefix, dot decimal
  //   it:       (+$9,99)   ← "$" prefix, comma decimal
  //   fr/de:    (+9,99 $)  ← "$" suffix (with space), comma decimal
  // We match any of these so a shopper on any language pays the correct total.
  function _parseUplift(s) {
    // Prefix-$ form: "+$9.99" or "+$9,99"
    var m = s.match(/\+\$\s*([0-9]+(?:[.,][0-9]{1,2})?)/);
    if (m) return parseFloat(m[1].replace(',', '.'));
    // Suffix-$ form: "+9,99 $" or "+9.99$"
    m = s.match(/\+\s*([0-9]+(?:[.,][0-9]{1,2})?)\s*\$/);
    if (m) return parseFloat(m[1].replace(',', '.'));
    return null;
  }

  function _parseOverride(s) {
    // Absolute per-variant price (no leading "+") — used by CapKit.
    // Prefix-$: "($29.99)" / "($29,99)"
    var m = s.match(/\(\$\s*([0-9]+(?:[.,][0-9]{1,2})?)\)/);
    if (m) return parseFloat(m[1].replace(',', '.'));
    // Suffix-$: "(29,99 $)" — same shape as fr/de uplift but WITHOUT the "+"
    m = s.match(/\(\s*([0-9]+(?:[.,][0-9]{1,2})?)\s*\$\s*\)/);
    if (m) return parseFloat(m[1].replace(',', '.'));
    return null;
  }

  for (var key in selections) {
    var val = selections[key];
    if (!val) continue;
    hasAnySelection = true;

    var lower = val.toLowerCase();

    // BelloFlex: custom color pricing supersedes base price.
    if (lower.indexOf('custom color') !== -1 && lower.indexOf('no custom') === -1) {
      total = 99.99;
    } else if (lower.indexOf('no custom color') !== -1) {
      total = 74.99;
    }

    // Generic per-variant uplift ("+$X" or "+X $").
    var uplift = _parseUplift(val);
    if (uplift !== null && isFinite(uplift)) {
      addons += uplift;
    } else {
      // Only try the override pattern if no uplift matched, so we don't
      // double-count "(+9,99 $)" as both an uplift AND an override.
      var override = _parseOverride(val);
      if (override !== null && isFinite(override)) {
        overridePrice = override;
      }
    }
  }

  if (!hasAnySelection) return null;
  if (overridePrice !== null) return overridePrice + addons;
  return total + addons;
}

function VariantSelectors(props) {
  var product = props.product;
  var formatPrice = props.formatPrice;
  var language = props.language || 'en';
  var onInstrumentChange = props.onInstrumentChange;  // legacy: NoteTurner-style instrument variant
  var onVariantChange = props.onVariantChange;        // generic: fires on any variant pick
  var onSelectionsChange = props.onSelectionsChange;  // (selections, computedTotal) — fires on every change
  var selectionsState = React.useState({});
  var selections = selectionsState[0];
  var setSelections = selectionsState[1];

  if (!product.variants) return null;

  function handleChange(groupIndex, value) {
    // Resolve the group NAME (e.g. "INSTRUMENT", "BATTERY") so downstream
    // consumers (validation, cart label) can look up selections by name.
    // ProductPage.variantsAreComplete() depends on this — keying by index
    // here was breaking Add-to-Cart for every multi-variant product.
    var groupName = null;
    try {
      if (product.variants && product.variants.groups && product.variants.groups[groupIndex]) {
        groupName = product.variants.groups[groupIndex].name;
      }
    } catch (e) { /* ignore */ }
    var key = groupName || groupIndex;

    var updated = {};
    for (var k in selections) updated[k] = selections[k];
    updated[key] = value;
    setSelections(updated);

    // If this is the MODEL TYPE group (index 0), notify parent for related products
    if (groupIndex === 0 && onInstrumentChange) {
      onInstrumentChange(value);
    }
    // Generic: notify on ANY variant change so the gallery can swap to the
    // variant-specific image (used by all multi-variant products like BelloFlex).
    if (onVariantChange) {
      onVariantChange(groupIndex, value, groupName);
    }
    // Bubble up the full selection map + computed total so the parent can pass
    // a correct unit price + composite variant label to the cart.
    if (onSelectionsChange) {
      try {
        var total = calcTotal(updated, product.price);
        onSelectionsChange(updated, total);
      } catch (e) { /* ignore */ }
    }
  }

  if (product.variants.groups) {
    // Use translated variants if available
    var groups = product.variants.groups;
    if (product.variantsByLanguage && product.variantsByLanguage[language] && product.variantsByLanguage[language].groups) {
      groups = product.variantsByLanguage[language].groups;
    }

    var total = calcTotal(selections, product.price);

    var groupEls = [];
    for (var g = 0; g < groups.length; g++) {
      groupEls.push(React.createElement(VariantGroup, { key: g, group: groups[g], index: g, onChange: handleChange, formatPrice: formatPrice }));
    }

    var totalEl = null;
    if (total !== null && formatPrice) {
      totalEl = React.createElement('div', { 'data-testid': 'variant-total', className: 'mt-6 p-4 rounded-xl bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-between' },
        React.createElement('span', { className: 'text-white font-semibold text-lg' }, 'Estimated Total'),
        React.createElement('span', { className: 'text-[#d4af37] font-bold text-2xl' }, formatPrice(total))
      );
    }

    return React.createElement('div', { className: 'mb-8 space-y-5' }, groupEls, totalEl);
  }

  if (product.variants.options && product.variants.options.length > 0) {
    // Use language-specific options if available (falls back to English source)
    var singleOpts = product.variants.options;
    if (product.variantsByLanguage && product.variantsByLanguage[language]
        && Array.isArray(product.variantsByLanguage[language].options)
        && product.variantsByLanguage[language].options.length === singleOpts.length) {
      singleOpts = product.variantsByLanguage[language].options;
    }

    function handleSingleChange(e) {
      var val = e.target.value;
      // Propagate up so the parent updates its `selectedVariant` state, which
      // is what `variantsAreComplete()` checks for single-list variants.
      // groupIndex=0 mirrors the multi-group signature so downstream logic
      // (related-products keying off instrument, etc.) keeps working.
      if (onVariantChange) onVariantChange(0, val, product.variants.type);
      // Also feed the parent's `multiSelections` + `multiPrice` state so the
      // big price header at the top of the product page updates when the
      // shopper picks a variant that overrides the base price (e.g.
      // MuteClip's `4 Pack (free box) ($34.99)`).
      if (onSelectionsChange) {
        var key = product.variants.type || 'variant';
        var selObj = {};
        if (val) selObj[key] = val;
        var total = calcTotal(selObj, product.price);
        onSelectionsChange(selObj, total);
      }
    }

    var opts = [React.createElement('option', { key: '__empty', value: '' }, 'Select')];
    for (var j = 0; j < singleOpts.length; j++) {
      var rawOpt = singleOpts[j];
      var displayOpt = reformatPricesInLabel(rawOpt, formatPrice);
      opts.push(React.createElement('option', { key: rawOpt, value: rawOpt }, displayOpt));
    }
    return React.createElement('div', { className: 'mb-8' },
      React.createElement('label', { className: 'text-xs uppercase tracking-widest text-neutral-600 mb-3 font-semibold block' },
        product.variants.type, ' ', React.createElement('span', { className: 'text-red-400' }, '*')
      ),
      React.createElement('div', { className: 'relative' },
        React.createElement('select', {
          'data-testid': 'variant-select',
          onChange: handleSingleChange,
          className: 'w-full appearance-none bg-neutral-900 border border-white/10 rounded-xl px-5 py-4 text-white text-lg focus:border-[#d4af37] focus:outline-none cursor-pointer'
        }, opts),
        React.createElement(ChevronDown, { className: 'absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400 pointer-events-none' })
      )
    );
  }

  return null;
}

function PricingBreakdown(props) {
  var product = props.product;
  var formatPrice = props.formatPrice;

  if (!product.variants || !product.variants.groups) return null;
  // Only show detailed breakdown for BelloFlex
  var hasMaterial = false;
  for (var i = 0; i < product.variants.groups.length; i++) {
    if (product.variants.groups[i].name === 'MATERIAL') hasMaterial = true;
  }
  if (!hasMaterial) return null;

  return React.createElement('div', { className: 'mb-10 p-5 bg-neutral-900/50 rounded-xl border border-white/5' },
    React.createElement('div', { className: 'grid grid-cols-1 gap-2 text-sm' },
      React.createElement('div', { className: 'flex justify-between text-neutral-400' },
        React.createElement('span', null, 'Any mute (Cup, Straight or Harmon) — no custom color'),
        React.createElement('span', { className: 'text-white font-medium' }, formatPrice(74.99))
      ),
      React.createElement('div', { className: 'flex justify-between text-neutral-400' },
        React.createElement('span', null, 'Custom color (includes MuteClip; Harmon also gets stem + MuteMute)'),
        React.createElement('span', { className: 'text-white font-medium' }, formatPrice(99.99))
      )
    )
  );
}

export { VariantSelectors, PricingBreakdown };
